import React, { useEffect } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import {
	ApplicationDetail,
	Applications,
	Auth,
	EmailTemplate,
	EmailTemplateDetail,
} from './base-ui/lib.js';
import { useApi } from './hooks/useApi.js';
import { cleanupStaleQuoteDrafts } from './utils/localStorageCleanup';
import AssemblyDetail from './pages/assembly/AssemblyDetail.jsx';
import AssemblyPage from './pages/assembly/index.jsx';
import { UserSettings } from './pages/Profile.js';
import TeamMember from './pages/team/TeamMember.js';
import Material from './pages/material/index.jsx';
import MaterialDetail from './pages/material/MaterialDetail.jsx';
import {
	useGlobalDispatch,
	useGlobalState,
} from './stateProviders/GlobalStateProvider.js';
import { QuotesPage } from './pages/quotations/QuotesPage.js';
import { PrintQuote } from './pages/printQuote/printQuote.js';
import { Companies } from './pages/companies/companies.js';
import { Company } from './pages/company/Company.js';
import { IntercomWrapper } from './intercomWrapper.js';
import { DashboardLayout } from './globalComponents/DashboardLayout.js';
import SignInPerson from './pages/quotations/views/Signing/signInPerson.js';
import SignerCompleted from './pages/quotations/views/Signing/signerCompleted.js';
import { QuotationsCountProvider } from './contexts/QuotationsCountContext.tsx';
import { Unauthorized } from './pages/Unauthorized.tsx';

const ProtectedAssemblyRoute: React.FC<{ children: React.ReactElement }> = ({
	children,
}) => {
	const globalState = useGlobalState();
	const isCompanyAdmin =
		globalState.appUser?.role?.roleName === 'Company Admin';
	if (isCompanyAdmin) {
		return <Unauthorized />;
	}

	return children;
};

export const App = () => {
	const globalDispatch = useGlobalDispatch();
	const globalState = useGlobalState();
	const api = useApi();

	useEffect(() => {
		const cleanedCount = cleanupStaleQuoteDrafts();
		if (cleanedCount > 0) {
		}
	}, []);

	useEffect(() => {
		(async () => {
			const appUser = await api.appUser.getMe();
			const company = await api.company.getCurrent();
			globalDispatch({
				appUser,
				company,
			});
		})();
	}, []);

	return (
		<Auth
			LoginHeaderLogo={'/deck_metrix_logo.svg'}
			signInBackgroundColor="transparent"
		>
			<IntercomWrapper>
				<Routes>
					{/* Render a layout route so navbars have access to params */}
					<Route
						path="/"
						element={
							<QuotationsCountProvider>
								<DashboardLayout />
							</QuotationsCountProvider>
						}
					>
						{/* App Routes go here */}
						<Route index element={<Navigate to="/quotations" replace />} />
						<Route path="/settings" element={<UserSettings />} />
						<Route
							path="/assembly"
							element={
								<ProtectedAssemblyRoute>
									<AssemblyPage />
								</ProtectedAssemblyRoute>
							}
						/>
						<Route path="/quotations/*" element={<QuotesPage />} />
						<Route
							path="/assembly/:assemblyId"
							element={
								<ProtectedAssemblyRoute>
									<AssemblyDetail />
								</ProtectedAssemblyRoute>
							}
						/>
						{/* Platform Routes */}
						<Route element={<Applications />} path="/applications" />
						<Route
							path="/application/:applicationId"
							element={<ApplicationDetail />}
						/>
						<Route path="/email/template" element={<EmailTemplate />} />
						<Route
							path="/email/template/:emailTemplateId"
							element={<EmailTemplateDetail />}
						/>
						{/* Dummy routes */}
						<Route path="/team-members" element={<TeamMember />} />
						<Route path="/users" element={<TeamMember />} />
						<Route path="/company/:companyId/*" element={<Company />} />
						<Route path="/companies" element={<Companies />} />
						<Route path="/materials" element={<Material />} />
						<Route path="/material/:materialId" element={<MaterialDetail />} />
						{/* <Route path="/joblog/*" element={<JobLogPage />} /> */}
						<Route path="/email/template" element={<EmailTemplate />} />
						<Route
							path="/email/template/:emailTemplateId"
							element={<EmailTemplateDetail />}
						/>
						<Route
							path="/signInPerson/:processId/:signerId"
							element={<SignInPerson />}
						/>
						<Route
							path="/signerCompleted/:processId/:signerId"
							element={<SignerCompleted />}
						/>
					</Route>
					<Route path="/print/:quoteId" element={<PrintQuote />} />
				</Routes>
			</IntercomWrapper>
		</Auth>
	);
};

export default App;
